import React, { useState, useEffect, useRef } from 'react';
import API, { BACKEND_URL } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { Send, Paperclip, Mic, MicOff, Image, FileText, Sparkles, Bot } from 'lucide-react';

export default function CantiereChat({ cantiereId }) {
  const { user } = useAuth();
  const [messaggi, setMessaggi] = useState([]);
  const [testo, setTesto] = useState('');
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [aiMode, setAiMode] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const endRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const fileInputRef = useRef(null);

  const loadMessages = () => API.get(`/api/cantieri/${cantiereId}/messaggi`).then(r => setMessaggi(r.data));
  useEffect(() => { loadMessages(); const iv = setInterval(loadMessages, 5000); return () => clearInterval(iv); }, [cantiereId]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messaggi]);

  const sendText = async () => {
    if (!testo.trim()) return;

    if (aiMode) {
      setAiLoading(true);
      const context = messaggi.slice(-10).map(m => `${m.utente_nome}: ${m.testo}`).join('\n');
      await API.post(`/api/cantieri/${cantiereId}/messaggi`, { testo, tipo_messaggio: 'testo' });

      try {
        const { data } = await API.post('/api/ai/assistente', { prompt: testo, contesto: context, tipo: 'cantiere' });
        await API.post(`/api/cantieri/${cantiereId}/messaggi`, { testo: data.risposta, tipo_messaggio: 'sistema' });
      } catch {
        await API.post(`/api/cantieri/${cantiereId}/messaggi`, { testo: 'Errore nella risposta AI', tipo_messaggio: 'sistema' });
      }
      setAiLoading(false);
    } else {
      await API.post(`/api/cantieri/${cantiereId}/messaggi`, { testo, tipo_messaggio: 'testo' });
    }
    setTesto('');
    loadMessages();
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const formData = new FormData();
        formData.append('file', blob, 'audio.webm');

        setLoading(true);
        try {
          const uploadRes = await API.post('/api/upload', formData);
          const transcribeRes = await API.post('/api/ai/transcribe', formData);
          await API.post(`/api/cantieri/${cantiereId}/messaggi`, {
            testo: transcribeRes.data.testo || '[Audio]',
            tipo_messaggio: 'audio',
            allegato_url: uploadRes.data.url,
            allegato_nome: 'audio.webm',
            trascrizione: transcribeRes.data.testo
          });
          loadMessages();
        } catch (e) {
          const formData2 = new FormData();
          formData2.append('file', blob, 'audio.webm');
          try {
            const uploadRes = await API.post('/api/upload', formData2);
            await API.post(`/api/cantieri/${cantiereId}/messaggi`, {
              testo: '[Audio - trascrizione non disponibile]',
              tipo_messaggio: 'audio',
              allegato_url: uploadRes.data.url,
              allegato_nome: 'audio.webm'
            });
            loadMessages();
          } catch {} 
        }
        setLoading(false);
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
    } catch {
      alert('Impossibile accedere al microfono');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  const uploadFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await API.post('/api/upload', formData);
      const tipo = file.type.startsWith('image/') ? 'foto' : 'documento';
      await API.post(`/api/cantieri/${cantiereId}/messaggi`, {
        testo: file.name,
        tipo_messaggio: tipo,
        allegato_url: data.url,
        allegato_nome: file.name
      });
      loadMessages();
    } catch {}
    setLoading(false);
  };

  const renderMessage = (m) => {
    const isMe = m.utente_id === user?._id;
    const isSys = m.tipo_messaggio === 'sistema';

    return (
      <div key={m.id} className={`flex ${isMe && !isSys ? 'justify-end' : 'justify-start'} mb-3`} data-testid={`msg-${m.id}`}>
        <div className={`max-w-[75%] rounded-lg px-4 py-2.5 ${isSys ? 'bg-white border border-slate-200' : isMe ? 'bg-slate-100' : 'bg-white border border-slate-200'}`}>
          <div className="flex items-center gap-2 mb-1">
            {isSys && <Bot size={14} className="text-primary" />}
            <span className="text-xs font-bold text-slate-500">{isSys ? 'EDILCIMA AI' : m.utente_nome}</span>
            <span className="text-xs text-slate-300">{new Date(m.created_at).toLocaleTimeString('it-IT', {hour:'2-digit', minute:'2-digit'})}</span>
          </div>
          {m.tipo_messaggio === 'foto' && m.allegato_url && (
            <img src={`${BACKEND_URL}${m.allegato_url}`} alt="Foto" className="rounded-md max-h-48 mb-2" />
          )}
          {m.tipo_messaggio === 'audio' && (
            <div className="mb-2">
              {m.allegato_url && <audio controls src={`${BACKEND_URL}${m.allegato_url}`} className="w-full h-8" />}
              {m.trascrizione && <p className="text-xs text-slate-500 mt-1 font-mono bg-slate-50 p-2 rounded">Trascrizione: {m.trascrizione}</p>}
            </div>
          )}
          {m.tipo_messaggio === 'documento' && m.allegato_url && (
            <a href={`${BACKEND_URL}${m.allegato_url}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-primary text-xs font-semibold mb-2 hover:underline">
              <FileText size={14}/> {m.allegato_nome || 'Documento'}
            </a>
          )}
          <p className={`text-sm whitespace-pre-wrap ${isSys ? 'text-slate-700' : ''}`}>{m.testo}</p>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full bg-surface rounded-md border border-slate-200" data-testid="cantiere-chat">
      <div className="flex items-center gap-2 px-4 py-2 border-b border-slate-200 bg-white rounded-t-md">
        <MessageCircleIcon className="text-primary" />
        <span className="font-bold text-sm">Chat Cantiere</span>
        <div className="flex-1" />
        <button data-testid="ai-toggle" onClick={() => setAiMode(!aiMode)}
          className={`px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1.5 transition-all ${aiMode ? 'bg-primary text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
          <Sparkles size={12} /> {aiMode ? 'AI Attivo' : 'AI'}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-1" data-testid="chat-messages">
        {messaggi.length === 0 && <p className="text-center text-slate-400 text-sm py-8">Nessun messaggio. Inizia la conversazione!</p>}
        {messaggi.map(renderMessage)}
        {(aiLoading) && <div className="flex justify-start"><div className="bg-white border border-slate-200 rounded-lg px-4 py-3"><div className="flex items-center gap-2 text-sm text-slate-500"><Bot size={16} className="animate-pulse text-primary"/>EDILCIMA AI sta rispondendo...</div></div></div>}
        <div ref={endRef} />
      </div>

      <div className="p-3 border-t border-slate-200 bg-white rounded-b-md">
        <div className="flex items-center gap-2">
          <input type="file" ref={fileInputRef} className="hidden" onChange={uploadFile} accept="image/*,.pdf,.doc,.docx,.xls,.xlsx" />
          <button onClick={() => fileInputRef.current?.click()} className="p-2 hover:bg-slate-100 rounded-md transition-colors" data-testid="upload-btn" title="Carica file">
            <Paperclip size={18} className="text-slate-400" />
          </button>
          <button onClick={() => { const inp = document.createElement('input'); inp.type='file'; inp.accept='image/*'; inp.onchange = (e) => uploadFile(e); inp.click(); }}
            className="p-2 hover:bg-slate-100 rounded-md transition-colors" data-testid="photo-btn" title="Foto">
            <Image size={18} className="text-slate-400" />
          </button>
          <button onClick={recording ? stopRecording : startRecording} data-testid="mic-btn"
            className={`p-2 rounded-md transition-all ${recording ? 'bg-red-500 text-white animate-pulse' : 'hover:bg-slate-100'}`} title="Registra audio">
            {recording ? <MicOff size={18} /> : <Mic size={18} className="text-slate-400" />}
          </button>
          <input data-testid="chat-input" placeholder={aiMode ? "Chiedi all'AI..." : "Scrivi un messaggio..."} className="flex-1 px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            value={testo} onChange={e => setTesto(e.target.value)} onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendText()} disabled={loading} />
          <button onClick={sendText} disabled={loading || !testo.trim()} data-testid="send-btn"
            className="p-2 bg-primary text-white rounded-md hover:bg-primary-hover transition-all active:scale-95 disabled:opacity-50">
            <Send size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}

function MessageCircleIcon({ className }) {
  return <svg className={className} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"/></svg>;
}
